import { useTooltip } from "context/TooltipProvider";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { editWitnessProction2 as editWitnessProtection2 } from "services/witnessProtectionService";
import { WitnessProtection } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  protection: WitnessProtection;
  inline?: boolean;
  onClose: (refetch: boolean) => void;
};

const inputClass =
  "w-full border border-gray-300 rounded-lg bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500";
const labelClass = "block text-sm font-medium text-gray-700 mb-1";

const ProtectionType: FC<Props> = ({ protection, inline, onClose }) => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [proteactionType, setProtectionType] = useState("");

  const validateForm = () => {
    if (!proteactionType)
      return `${t("witness_protection.detail.protection_type")} ${t("required")}.`;
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const api = editWitnessProtection2;
    api({
      id: protection?.id ?? undefined,
      protection_type: proteactionType,
    })
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        onClose(true);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
        setLoading(false);
      });
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  const field = (
    <div>
      <label className={labelClass}>
        {t("witness_protection.detail.protection_type")}{" "}
        <span className="text-red-500">*</span>
      </label>
      <input
        className={inputClass}
        value={proteactionType}
        onChange={(e) => setProtectionType(e.target.value)}
        placeholder={t("witness_protection.modals.protection_type.title")}
      />
    </div>
  );

  const submitLabel = loading ? (
    <span className="flex items-center justify-center gap-2">
      {LoadingIcon}
      {t("loading")}
    </span>
  ) : (
    t("witness_protection.modals.protection_type.submit")
  );

  // Inline mode: render as a card inside the page (e.g. the right panel).
  if (inline) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 shrink-0 rounded-lg bg-gradient-to-br from-red-50 to-rose-100 flex items-center justify-center text-red-600 text-base">
              🔒
            </div>
            <h3 className="text-base font-semibold text-gray-900">
              {t("witness_protection.modals.protection_type.title")}
            </h3>
          </div>
          <button
            type="button"
            onClick={() => onClose(false)}
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            aria-label={t("cancel", { defaultValue: "Cancel" })}
          >
            ×
          </button>
        </div>
        <form className="p-5 space-y-4" onSubmit={handleSubmit}>
          {field}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="submit"
              className={`px-6 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={loading}
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {t("witness_protection.modals.protection_type.title")}
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          {t("witness_protection.modals.protection_type.form_description")}
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="mt-4">{field}</div>

          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2
                      text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                              ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
              disabled={loading}
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProtectionType;
